import * as $maths from "../gleam_community_maths/gleam_community/maths.mjs";
import * as $float from "../gleam_stdlib/gleam/float.mjs";
import { Ok, CustomType as $CustomType, makeError, divideFloat } from "./gleam.mjs";

const FILEPATH = "src/vector.gleam";

export class Vector extends $CustomType {
  constructor(x, y, z) {
    super();
    this.x = x;
    this.y = y;
    this.z = z;
  }
}
export const Vector$Vector = (x, y, z) => new Vector(x, y, z);
export const Vector$isVector = (value) => value instanceof Vector;
export const Vector$Vector$x = (value) => value.x;
export const Vector$Vector$0 = (value) => value.x;
export const Vector$Vector$y = (value) => value.y;
export const Vector$Vector$1 = (value) => value.y;
export const Vector$Vector$z = (value) => value.z;
export const Vector$Vector$2 = (value) => value.z;

/**
 * Adds 2 vectors together.
 */
export function add(v1, v2) {
  return new Vector(v1.x + v2.x, v1.y + v2.y, v1.z + v2.z);
}

/**
 * Subtracts the second vector from the first vector.
 */
export function subtract(v1, v2) {
  return new Vector(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
}

/**
 * Computes the dot product of 2 vectors.
 */
export function dot(v1, v2) {
  return ((v1.x * v2.x) + (v1.y * v2.y)) + (v1.z * v2.z);
}

/**
 * Computes the cross product of 2 vectors.
 */
export function cross(v1, v2) {
  let yz = (v1.y * v2.z) - (v1.z * v2.y);
  let zx = (v1.z * v2.x) - (v1.x * v2.z);
  let xy = (v1.x * v2.y) - (v1.y * v2.x);
  return new Vector(yz, zx, xy);
}

/**
 * Multiplies the vector by some value.
 */
export function multiply(v, mult) {
  return new Vector(v.x * mult, v.y * mult, v.z * mult);
}

/**
 * Divides the vector by some value.
 */
export function divide(v, mult) {
  return new Vector(
    divideFloat(v.x, mult),
    divideFloat(v.y, mult),
    divideFloat(v.z, mult),
  );
}

/**
 * Computes the square of the magnitude of the vector.
 * Mainly used for performance when computing square roots is unnecessary.
 */
export function magnitude_squared(v) {
  return ((v.x * v.x) + (v.y * v.y)) + (v.z * v.z);
}

/**
 * Computes the magnitude of the vector.
 */
export function magnitude(v) {
  let $ = $float.square_root(magnitude_squared(v));
  let mag;
  if ($ instanceof Ok) {
    mag = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "vector",
      51,
      "magnitude",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1435,
        end: 1495,
        pattern_start: 1446,
        pattern_end: 1453
      }
    )
  }
  return mag;
}

/**
 * Computes a vector of the same heading with a magnitude of 1.
 */
export function normalize(v) {
  return divide(v, magnitude(v));
}

/**
 * Computes a vector of the same heading with maximum magnitude.
 */
export function limit(v, limit) {
  let lim_squared = limit * limit;
  let $ = magnitude_squared(v);
  let m = $;
  if (m <= lim_squared) {
    return v;
  } else {
    return multiply(normalize(v), limit);
  }
}

/**
 * Computes the distance between 2 vectors.
 */
export function distance(v1, v2) {
  return magnitude(subtract(v1, v2));
}

/**
 * Creates a 2d version of the vector.
 */
export function vector_2d(v) {
  return new Vector(v.x, v.y, 0.0);
}

/**
 * Computes the heading in radians of a vector.
 */
export function heading2d(v) {
  let res = $maths.atan2(v.y, v.x);
  let r = res;
  if (r < 0.0) {
    return r + (2.0 * $maths.pi());
  } else {
    return res;
  }
}

/**
 * Rotates a vector around the z axis by the given amount in radians.
 */
export function rotate2d(v, rotation) {
  let heading = heading2d(v) + rotation;
  let mag = magnitude(vector_2d(v));
  return new Vector($maths.cos(heading) * mag, $maths.sin(heading) * mag, v.z);
}

/**
 * Create a vector around the z axis by the given amount in radians.
 */
export function from_angle2d(rotation) {
  return rotate2d(new Vector(1.0, 0.0, 0.0), rotation);
}
